/**
 * The welcome galaxy's two looks (`welcomeGalaxyPalette`), which follow the app's theme
 * (`useTheme`), so that entering the app never switches from one scheme to the other.
 *
 * On both, the galaxy itself is untinted: its characters and stars are white at the core and darken
 * through greys to black out at the rim (`DISK_COLORS`), under white light at the core and a faint
 * grey halo.
 *
 * Every layer meets the sky in a blend the GPU does in fixed function (`plus`, `screen` or plain
 * `srcOver`): multiplying or lightening would make it read back what is already drawn, which many
 * phones' GPUs can only do by copying the screen. Over the opaque sky, multiplying by a grey is
 * exactly laying black over it (at the grey's darkness), so that is how the darkening layers draw.
 *
 * - `dark`: the galaxy as light on deep space, which its rim fades out into. Every sprite, warp
 *   streak and the core glow are added onto the sky (`plus`, `screen`), and the dust lanes darken
 *   it (black, laid over it).
 * - `light`: the galaxy painted in ink on warm off-white paper, which the launch eases into from
 *   the splash screen's white. Its rim is black ink, the characters streaming past are black and
 *   graphite ink, the background's stars are tiny graphite pinyin tone marks (ˉ ˊ ˇ ˋ), and its
 *   core's light, brighter than the paper, is what the innermost characters dissolve into. Ink is
 *   laid on rather than added (`srcOver`), so however densely the core's characters and stars
 *   crowd together they deepen only to their own shades, never to a blot; the sprites' soft glow
 *   becomes ink bleeding into the paper. The faint halo and the warp streaks are the dark galaxy's
 *   negatives (`welcomeGalaxyInkColor`), the halo washed on (black, as multiplying by its grey),
 *   and the dust lanes lift the arms' ink back to bare paper instead of darkening them (laid on in
 *   the paper's colour), like the streaks of flying white in a dry brush stroke.
 */

import onetime from "@-/com.npmjs.onetime";

export type WelcomeGalaxyAppearance = "dark" | "light";

/** Red, green and blue, 0–255. */
export type WelcomeGalaxyRgb = readonly [red: number, green: number, blue: number];
/** Red, green and blue, 0–1. */
export type WelcomeGalaxyChannels = readonly [red: number, green: number, blue: number];
/** Red, green and blue, 0–255, with an alpha of 0–1. */
type WelcomeGalaxyRgba = readonly [red: number, green: number, blue: number, alpha: number];

/**
 * Each kind of particle's colour, from one end of its range to the other (channels 0–1): for the
 * disk's characters and stars, at the very core and out at the rim (a smootherstep over the disk's
 * radius); for the background stars and the stream, the deeper and paler of two shades, mixed at
 * random.
 */
export type WelcomeGalaxyParticleColors = {
  diskCore: WelcomeGalaxyChannels;
  diskRim: WelcomeGalaxyChannels;
  fieldDeep: WelcomeGalaxyChannels;
  fieldPale: WelcomeGalaxyChannels;
  /** The characters streaming past the camera (and the launch's, once out of the splash). */
  streamDeep: WelcomeGalaxyChannels;
  streamPale: WelcomeGalaxyChannels;
};

/** One stop of a radial gradient: its offset (0–1), colour and opacity. */
export type WelcomeGalaxyGradientStop = { alpha: number; offset: number; rgb: WelcomeGalaxyRgb };

/** A radial gradient, as Skia takes it (`colors`, `positions`) and as its `stops`. */
export type WelcomeGalaxyGradient = {
  colors: string[];
  positions: number[];
  stops: WelcomeGalaxyGradientStop[];
};

export type WelcomeGalaxyPalette = {
  appearance: WelcomeGalaxyAppearance;
  /** The core glow, out to 0.42 disk radii, added onto the sky (`plus`) on both galaxies. */
  core: WelcomeGalaxyGradient;
  /** The point at the very core (see `CORE_DOT_RADIUS` in `welcome-galaxy-scene.tsx`). */
  coreDot: WelcomeGalaxyGradient;
  /**
   * The core glow's opacity while the camera is still inside the bulge, where it covers the whole
   * screen: a hint of the core's light on the dark galaxy, but none on the light one, whose launch
   * has to start on exactly the splash screen's white.
   */
  coreFloorOpacity: number;
  /** The dust lanes' colour, baked into their texture, which is laid over the sky (`srcOver`). */
  dustLaneColor: string;
  /**
   * The Learn card the entry's galaxy becomes, as the app's theme paints it (`--color-surface`,
   * `--color-border`), as opaque 0xAARRGGBB: what the entry host draws with until it has read the
   * live theme tokens.
   */
  entryCard: { border: number; surface: number };
  /** How many background stars there are, relative to the platform's budget. */
  fieldStarDensity: number;
  /**
   * Whether the background's stars, sprinkled over the whole page, are pinyin tone marks
   * (`welcome-galaxy-tone-marks.ts`) rather than dots: star-sized, tilted and twinkling.
   */
  fieldStarsAreToneMarks: boolean;
  /** The faint halo, out to 1.15 disk radii. */
  halo: WelcomeGalaxyGradient;
  haloBlendMode: "screen" | "srcOver";
  /**
   * The sky's own colour as a class: the page behind the canvas until it draws, the dimming over
   * the galaxy behind the wordmark, and the fallback's backdrop. Its last `sky` colour.
   */
  pageClassName: "bg-background" | "bg-black";
  particleColors: WelcomeGalaxyParticleColors;
  /** Outward rays lose most of their ink/light near the core, then disappear entirely. */
  shootOutFade: WelcomeGalaxyGradient;
  /**
   * The sky the launch climbs through: the splash screen's white, a middle tone at the moment
   * the characters turn into the galaxy's, and the sky of the galaxy itself.
   */
  sky: readonly [WelcomeGalaxyRgb, WelcomeGalaxyRgb, WelcomeGalaxyRgb];
  /** The launch's speed lines once its characters have turned into the galaxy's. */
  speedLineAlpha: number;
  speedLineRgb: WelcomeGalaxyRgb;
  /**
   * The entry's spin streaks at their strongest: the arcs the disk's characters blur into as the
   * galaxy spins up, in the warp streaks' colour.
   */
  spinStreakAlpha: number;
  /** How every character and star (one `Atlas`), and the warp streaks, meet the sky. */
  spriteBlendMode: "plus" | "srcOver";
  /**
   * The background stars' warp streaks, at their strongest. The light galaxy has four times as
   * many background stars, so its streaks are fainter, or they would hatch over the whole screen.
   */
  streakAlpha: number;
  streakColor: string;
  /**
   * The magnifications over which a stream character fades out as it dwindles towards the
   * vanishing point: gone at the first, whole from the second. Over space the receding
   * characters pile into a glow there; on paper they would pile into a blot of ink, so the light
   * galaxy fades them out over far more of their recession, as distance fades an ink-wash
   * landscape into the paper.
   */
  streamFarFade: readonly [gone: number, whole: number];
  /**
   * How far the stream characters sit off the camera's flight path, relative to the dark galaxy's
   * tight stream. Strung tightly, they overlap right at the vanishing point even while large: a
   * glow over space, a pool of ink on paper. The light galaxy strings them into a wider tunnel, so
   * they pass around the centre rather than through it.
   */
  streamSpread: number;
};

/**
 * The colour with its HSL lightness inverted, keeping its hue and saturation: white becomes
 * black, a pale blue streak deep indigo, a violet glow lavender. HSL chroma depends on the
 * lightness only through |2L − 1|, so inverting it shifts every channel by the same
 * `1 − max − min`, and the new lightness is 1 − L. Channels 0–1.
 */
export function welcomeGalaxyInkColor(
  red: number,
  green: number,
  blue: number,
): [number, number, number] {
  const shift = 1 - Math.max(red, green, blue) - Math.min(red, green, blue);
  return [red + shift, green + shift, blue + shift];
}

/** `welcomeGalaxyInkColor` on 0–255 channels, rounded. */
function inkRgb([red, green, blue]: WelcomeGalaxyRgb): WelcomeGalaxyRgb {
  const [inkRed, inkGreen, inkBlue] = welcomeGalaxyInkColor(red / 255, green / 255, blue / 255);
  return [Math.round(inkRed * 255), Math.round(inkGreen * 255), Math.round(inkBlue * 255)];
}

/** A colour of the dark galaxy as `appearance` draws it: as it is, or as its negative. */
export function welcomeGalaxyRgb(
  appearance: WelcomeGalaxyAppearance,
  rgb: WelcomeGalaxyRgb,
): WelcomeGalaxyRgb {
  return appearance === "dark" ? rgb : inkRgb(rgb);
}

function cssColor([red, green, blue, alpha]: WelcomeGalaxyRgba): string {
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function gradient(
  colors: readonly WelcomeGalaxyRgba[],
  positions: number[],
): WelcomeGalaxyGradient {
  return {
    colors: colors.map((color) => cssColor(color)),
    positions,
    stops: colors.map(([red, green, blue, alpha], index) => ({
      alpha,
      offset: positions[index] ?? 1,
      rgb: [red, green, blue],
    })),
  };
}

/** The dark galaxy's faint grey halo, and the light galaxy's as its negative. */
const HALO_STOPS: WelcomeGalaxyRgba[] = [
  [136, 136, 136, 0.34],
  [72, 72, 72, 0.16],
  [36, 36, 36, 0.05],
  [0, 0, 0, 0],
];
const HALO_POSITIONS = [0, 0.45, 0.8, 1];
const WHITE: WelcomeGalaxyRgb = [255, 255, 255];
/**
 * The white-hot core glow, on both galaxies: added (`plus`) over the innermost characters until
 * they are white. On paper it is white light, brighter than the paper, rather than its negative,
 * a blot of ink that would read as a smudge under the wordmark.
 */
const CORE_STOPS: WelcomeGalaxyRgba[] = [
  [...WHITE, 1],
  [...WHITE, 0.55],
  [...WHITE, 0.16],
  [...WHITE, 0],
];
const CORE_POSITIONS = [0, 0.12, 0.45, 1];
/**
 * The point at the very core: the profile of a disk blurred on the disk plane (see
 * `CORE_DOT_RADIUS` in `welcome-galaxy-scene.tsx`), white-hot on both galaxies.
 */
const CORE_DOT_ALPHAS = [0.677, 0.584, 0.473, 0.339, 0.21, 0.112, 0.022, 0];
const CORE_DOT_POSITIONS = [0, 1 / 6, 1 / 4, 1 / 3, 5 / 12, 1 / 2, 2 / 3, 1];
const STREAK_RGB: WelcomeGalaxyRgb = [200, 215, 255];
/** The dark galaxy's dust lanes: black over the sky, as darkening it by multiplying by 24/255. */
const DARK_DUST_LANE_ALPHA = Math.round((1 - 24 / 255) * 1000) / 1000;
/** The dark launch's sky: the bootsplash's white, an atmospheric dusk, and deep space. */
const DARK_SKY = [WHITE, [58, 70, 110], [2, 3, 8]] as const;
/**
 * The light galaxy's paper matches the app's warm beige background (`bg-background`, #f9f7f3).
 * The ink reads better on it than on pure white, and the core's light shows as brighter still.
 */
const PAPER: WelcomeGalaxyRgb = [249, 247, 243];
/** The light launch's sky: the bootsplash's white, easing into the paper. */
const LIGHT_SKY = [WHITE, [251, 249, 246], PAPER] as const;
const SPEED_LINE_RGB: WelcomeGalaxyRgb = [205, 218, 255];

/**
 * The disk, on both galaxies: white at the core, darkening through greys to black out at the rim,
 * so that on space it fades out into the dark and on paper it ends in black ink. The characters
 * nearest the core crowd together into the vanishing point as the camera draws back, where a dark
 * ink would pool into a blot, but there they are white.
 */
const DISK_COLORS = { diskCore: [1, 1, 1], diskRim: [0, 0, 0] } as const;
/** Starlight: background stars and streaming characters in white and pale grey. */
const DARK_PARTICLE_COLORS: WelcomeGalaxyParticleColors = {
  ...DISK_COLORS,
  fieldDeep: [0.72, 0.72, 0.72],
  fieldPale: [1, 1, 1],
  streamDeep: [0.82, 0.82, 0.82],
  streamPale: [1, 1, 1],
};
/** Ink without pigment: graphite tone marks, and black and graphite streaming characters. */
const LIGHT_PARTICLE_COLORS: WelcomeGalaxyParticleColors = {
  ...DISK_COLORS,
  fieldDeep: [0.3, 0.3, 0.3],
  fieldPale: [0.6, 0.6, 0.6],
  streamDeep: [0, 0, 0],
  streamPale: [0.32, 0.32, 0.32],
};

function createWelcomeGalaxyPalette(appearance: WelcomeGalaxyAppearance): WelcomeGalaxyPalette {
  const negative = ([red, green, blue, alpha]: WelcomeGalaxyRgba): WelcomeGalaxyRgba => [
    ...welcomeGalaxyRgb(appearance, [red, green, blue]),
    alpha,
  ];
  const isDark = appearance === "dark";
  const [paperRed, paperGreen, paperBlue] = PAPER;
  return {
    appearance,
    core: gradient(CORE_STOPS, CORE_POSITIONS),
    coreDot: gradient(
      CORE_DOT_ALPHAS.map((alpha) => [...WHITE, alpha] as const),
      CORE_DOT_POSITIONS,
    ),
    coreFloorOpacity: isDark ? 0.04 : 0,
    dustLaneColor: isDark
      ? `rgba(0, 0, 0, ${DARK_DUST_LANE_ALPHA})`
      : `rgb(${paperRed}, ${paperGreen}, ${paperBlue})`,
    // `global.css`: dark surface 0 0% 3.9% / border 0 0% 14.9%; light surface white / border 0 0% 89.8%.
    entryCard: isDark
      ? { border: 0xff_26_26_26, surface: 0xff_0a_0a_0a }
      : { border: 0xff_e5_e5_e5, surface: 0xff_ff_ff_ff },
    fieldStarDensity: isDark ? 1 : 4,
    fieldStarsAreToneMarks: !isDark,
    // On paper, the halo's grey negative darkens it as black laid over it would, at the grey's darkness.
    halo: gradient(
      HALO_STOPS.map((stop) => {
        if (isDark) {
          return stop;
        }
        const ink = negative(stop);
        return [0, 0, 0, Math.round(ink[3] * (1 - ink[0] / 255) * 1000) / 1000] as const;
      }),
      HALO_POSITIONS,
    ),
    haloBlendMode: isDark ? "screen" : "srcOver",
    pageClassName: isDark ? "bg-black" : "bg-background",
    particleColors: isDark ? DARK_PARTICLE_COLORS : LIGHT_PARTICLE_COLORS,
    shootOutFade: gradient(
      [1, 0.25, 0.04, 0].map((alpha) => negative([...STREAK_RGB, alpha])),
      [0, 0.2, 0.5, 1],
    ),
    sky: isDark ? DARK_SKY : LIGHT_SKY,
    speedLineAlpha: 0.6,
    speedLineRgb: welcomeGalaxyRgb(appearance, SPEED_LINE_RGB),
    spinStreakAlpha: isDark ? 0.5 : 0.3,
    spriteBlendMode: isDark ? "plus" : "srcOver",
    streakAlpha: isDark ? 0.55 : 0.28,
    streakColor: cssColor(negative([...STREAK_RGB, 1])),
    streamFarFade: isDark ? [2.5, 6] : [5, 20],
    streamSpread: isDark ? 1 : 3,
  };
}

const getWelcomeGalaxyPalettes = onetime(
  (): Record<WelcomeGalaxyAppearance, WelcomeGalaxyPalette> => ({
    dark: createWelcomeGalaxyPalette("dark"),
    light: createWelcomeGalaxyPalette("light"),
  }),
);

/** The palette the welcome galaxy draws with in `appearance`: the same object every time. */
export function welcomeGalaxyPalette(appearance: WelcomeGalaxyAppearance): WelcomeGalaxyPalette {
  return getWelcomeGalaxyPalettes()[appearance];
}

/** `from` → `to` (channels 0–1), exactly as a `lerp` of each channel. */
export function welcomeGalaxyMixColor(
  from: WelcomeGalaxyChannels,
  to: WelcomeGalaxyChannels,
  progress: number,
): [number, number, number] {
  return [
    from[0] + (to[0] - from[0]) * progress,
    from[1] + (to[1] - from[1]) * progress,
    from[2] + (to[2] - from[2]) * progress,
  ];
}
